import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { appRoutes } from '@/app/appRoutes';
import { usePermissionsSet } from '@/entities/profile';
import type { RouteConfig } from '../types';

export type FlattenRoute = {
  label: string;
  path: string;
  icon?: React.ReactNode | string;
};

const checkParams = (path: string) => {
  return /\/:/.test(path);
};

const normalizePath = (p: string) => {
  if (!p) return '/';
  const withSlash = p.startsWith('/') ? p : `/${p}`;
  return withSlash.replace(/\/+/g, '/');
};

export const useFlattenRoutes = () => {
  const { i18n } = useTranslation();

  const myPermissions = usePermissionsSet();

  const hasPermission = useCallback((route: RouteConfig) => {
    const perms = [route.viewPermission].flat().filter(Boolean) as string[];
    if (perms.length === 0) return true; // routes without explicit permission are visible
    return perms.some((perm) => myPermissions.has(perm));
  }, []);

  const normalizeLabel = useCallback(
    (label: RouteConfig['label']) => (typeof label === 'function' ? label({ i18n }) : label),
    [],
  );

  return useMemo(() => {
    const result: FlattenRoute[] = [];

    const flatRoutes = (routes: RouteConfig[], parentPath: string) => {
      routes.forEach((route) => {
        const isGroup = route.type === 'group';
        const ownPath = route.path ? normalizePath(`${parentPath}/${route.path}`) : parentPath;
        const label = normalizeLabel(route.label);
        const resolvedPath = normalizePath(route.navbar?.path || ownPath);

        const hasParams = checkParams(resolvedPath);

        if (!hasParams && !isGroup && !!label && hasPermission(route)) {
          result.push({
            label: label,
            path: resolvedPath,
            icon: route.navbar?.icon,
          });
        }

        if (route.children?.length) {
          flatRoutes(route.children as RouteConfig[], ownPath);
        }
      });
    };

    flatRoutes(appRoutes as unknown as RouteConfig[], '');

    return result;
  }, [i18n, myPermissions, normalizeLabel, normalizePath]);
};
